import { useEffect, useState, type ReactElement } from "react";
import { X } from "lucide-react";
import { Badge, Button, IconButton, Stack, Surface, Text } from "@ux/primitives";
import { UserPicker, WorkspacePicker } from "@ux/compositions";
import { palette, radius, space } from "@ux/tokens";
import type { UserDirectoryEntry } from "../users/types";
import type { WorkspaceView } from "../workspaces/types";
import { TargetKindToggle } from "./TargetKindToggle";
import type { AssetAssignTarget } from "./types";

/**
 * Mode descriptor — controls heading copy and primary-button verb. Both
 * modes use exactly the same picker UI; only the framing changes:
 *
 *   - "assign"    — asset is currently unassigned; primary CTA "Assign"
 *   - "reassign"  — asset has an open assignment; primary CTA "Re-assign"
 *
 * Keeping these as discriminated cases (rather than a free-form string)
 * lets the dialog stay vocabulary-consistent with the rest of the app
 * (per the lockstep "Move" → "Re-assign" rename).
 */
export type AssignTargetDialogMode =
    | { readonly kind: "assign" }
    | { readonly kind: "reassign"; readonly currentTargetLabel: string };

export interface AssignTargetDialogProps {
    readonly open: boolean;
    readonly mode: AssignTargetDialogMode;
    /**
     * Initial picker kind. The caller should derive this from the asset's
     * type (Laptop → User, else Workspace) for "assign" or from the
     * current assignment's kind for "re-assign". Once the dialog is open
     * the user can flip it freely — unless `lockedKind` is also set, in
     * which case the toggle is hidden and the kind is fixed.
     */
    readonly defaultKind: AssetAssignTarget["kind"];
    /**
     * Optional. When set, the kind toggle is hidden and the dialog is
     * locked to a single picker kind. Used by callers that already know
     * the target kind and want a single-purpose modal — e.g. the
     * workspace detail page picking an occupant (always a User), or
     * future "assign to a specific user" flows.
     *
     * When set, `defaultKind` is ignored in favor of this value.
     */
    readonly lockedKind?: AssetAssignTarget["kind"];
    /**
     * Headline noun. For asset flows this is the asset label; for
     * workspace-occupant flows it's the workspace label. Plugged into
     * "Assign {label}" / "Re-assign {label}" copy.
     */
    readonly assetLabel: string;
    readonly onSubmit: (target: AssetAssignTarget) => void;
    readonly onCancel: () => void;
    readonly isWorking: boolean;
    readonly errorMessage?: string | null;
}

/**
 * Shared target-aware modal used by both the asset detail page (Assign /
 * Re-assign actions) and any future caller that needs "pick a workspace
 * OR a user." Internally state is per-kind: switching the toggle keeps
 * each side's selection so a user who flipped to "User", looked, then
 * flipped back to "Workspace" finds their workspace pick still there.
 */
export function AssignTargetDialog({
    open,
    mode,
    defaultKind,
    lockedKind,
    assetLabel,
    onSubmit,
    onCancel,
    isWorking,
    errorMessage,
}: AssignTargetDialogProps): ReactElement | null {
    // lockedKind wins if set; otherwise the user can flip via the toggle.
    const initialKind = lockedKind ?? defaultKind;
    const [kind, setKind] = useState<AssetAssignTarget["kind"]>(initialKind);
    const [workspace, setWorkspace] = useState<WorkspaceView | null>(null);
    const [user, setUser] = useState<UserDirectoryEntry | null>(null);

    // Reset on open: a stale pick from a prior invocation would surprise
    // the user. The initialKind also re-applies so opening Re-assign on a
    // user-target asset doesn't carry the prior workspace bias.
    useEffect(() => {
        if (open) {
            setKind(initialKind);
            setWorkspace(null);
            setUser(null);
        }
    }, [open, initialKind]);

    if (!open) return null;

    const selectedTarget: AssetAssignTarget | null =
        kind === "Workspace" && workspace !== null
            ? { kind: "Workspace", id: workspace.id }
            : kind === "User" && user !== null
              ? { kind: "User", id: user.id }
              : null;

    const isReassign = mode.kind === "reassign";
    const heading = isReassign ? `Re-assign ${assetLabel}` : `Assign ${assetLabel}`;
    const primaryLabel = isReassign
        ? isWorking
            ? "Re-assigning…"
            : "Re-assign"
        : isWorking
          ? "Assigning…"
          : "Assign";

    function handleSubmit(): void {
        if (selectedTarget === null) return;
        onSubmit(selectedTarget);
    }

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={heading}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: space[4],
                zIndex: 50,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isWorking) onCancel();
            }}
        >
            <div
                style={{
                    background: palette.canvas,
                    border: `1px solid ${palette.border}`,
                    borderRadius: radius.lg,
                    padding: space[5],
                    width: "min(560px, 100%)",
                    maxHeight: "min(80vh, 720px)",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                }}
            >
                <Stack direction="row" align="center" justify="space-between" gap={2}>
                    <Stack gap={1}>
                        <Text role="heading">{heading}</Text>
                        {isReassign ? (
                            <Text role="muted">Currently {mode.currentTargetLabel}</Text>
                        ) : (
                            <Text role="muted">
                                {lockedKind === "User"
                                    ? "Pick a user."
                                    : lockedKind === "Workspace"
                                      ? "Pick a workspace."
                                      : "Pick a workspace or a user."}
                            </Text>
                        )}
                    </Stack>
                    <IconButton
                        icon={<X size={16} />}
                        label="Close"
                        tone="neutral"
                        onClick={onCancel}
                        disabled={isWorking}
                    />
                </Stack>

                {/*
                    Body intentionally uses `overflow: visible`, NOT
                    `overflow: auto`. The picker's typeahead dropdown is
                    `position: absolute` and opens below the input — an
                    overflow:auto parent would clip it flush against the
                    input, hiding every result. Body content here is
                    bounded (toggle + picker + small error/badge) and
                    safely fits inside the card's maxHeight, so we can
                    skip the scroll container without regressing layout.
                */}
                <div style={{ marginTop: space[4], flex: 1, minHeight: 0 }}>
                    <Stack gap={3}>
                        {/* Toggle hides when the caller has locked the
                            kind — the dialog becomes a single-purpose
                            picker (e.g. workspace-occupant flow). */}
                        {lockedKind === undefined ? (
                            <TargetKindToggle
                                value={kind}
                                onChange={setKind}
                                disabled={isWorking}
                            />
                        ) : null}
                        {kind === "Workspace" ? (
                            <WorkspacePicker
                                value={workspace}
                                onChange={setWorkspace}
                                disabled={isWorking}
                            />
                        ) : (
                            <UserPicker
                                value={user}
                                onChange={setUser}
                                disabled={isWorking}
                            />
                        )}
                        {errorMessage !== null && errorMessage !== undefined ? (
                            <Surface tone="raised" padding={3}>
                                <Text role="muted" color={palette.danger}>
                                    {errorMessage}
                                </Text>
                            </Surface>
                        ) : null}
                        {selectedTarget !== null ? (
                            <Stack direction="row" gap={2} align="center">
                                <Badge tone="accent">Ready to {isReassign ? "re-assign" : "assign"}</Badge>
                            </Stack>
                        ) : null}
                    </Stack>
                </div>

                <div style={{ marginTop: space[4] }}>
                    <Stack direction="row" justify="flex-end" gap={2}>
                        <Button variant="ghost" onClick={onCancel} disabled={isWorking}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={isWorking || selectedTarget === null}
                        >
                            {primaryLabel}
                        </Button>
                    </Stack>
                </div>
            </div>
        </div>
    );
}
